import { useEffect, useState, useCallback } from "react";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Eye, EyeOff, Trash2, Pin, RotateCcw, Flag, UserX, Clock, ShieldOff, History } from "lucide-react";

export default function AdminCommunity() {
  const [view, setView] = useState("posts"); // posts | comments | modlog
  const [posts, setPosts] = useState([]);
  const [comments, setComments] = useState([]);
  const [logs, setLogs] = useState([]);

  const load = useCallback(async () => {
    try {
      if (view === "posts") setPosts((await api.get("/community-space/admin/reports?kind=post")).data.posts || []);
      else if (view === "comments") setComments((await api.get("/community-space/admin/reports?kind=comment")).data.comments || []);
      else setLogs((await api.get("/community-space/admin/modlog")).data.logs || []);
    } catch (e) { toast.error(apiErr(e)); }
  }, [view]);
  useEffect(() => { load(); }, [load]);

  const modPost = async (id, action) => { try { await api.patch(`/community-space/admin/posts/${id}?action=${action}`); toast.success("Action effectuée."); load(); } catch (e) { toast.error(apiErr(e)); } };
  const modComment = async (id, action) => { try { await api.patch(`/community-space/admin/comments/${id}?action=${action}`); toast.success("Action effectuée."); load(); } catch (e) { toast.error(apiErr(e)); } };

  const moderateUser = async (userId, action) => {
    let days = 7, reason = "";
    if (action === "suspend") { days = parseInt(window.prompt("Suspendre pour combien de jours ?", "7") || "7", 10); reason = window.prompt("Motif (optionnel) :") || ""; }
    if (action === "ban") { if (!window.confirm("Bannir définitivement cet utilisateur de l'espace communautaire ?")) return; reason = window.prompt("Motif (optionnel) :") || ""; }
    try { await api.post(`/community-space/admin/users/${userId}/moderate`, { action, days, reason }); toast.success("Modération appliquée."); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const UserActions = ({ uid }) => uid ? (
    <div className="flex gap-1.5 flex-wrap mt-2 pt-2 border-t border-white/5">
      <Btn onClick={() => moderateUser(uid, "suspend")} icon={Clock} label="Suspendre l'auteur" />
      <Btn onClick={() => moderateUser(uid, "ban")} icon={UserX} label="Bannir l'auteur" tone="red" />
      <Btn onClick={() => moderateUser(uid, "unban")} icon={ShieldOff} label="Lever ban/suspension" tone="green" />
    </div>
  ) : null;

  return (
    <div className="space-y-4" data-testid="admin-community">
      <div className="flex gap-2">
        {[["posts", "Sujets signalés"], ["comments", "Commentaires signalés"], ["modlog", "Historique"]].map(([v, l]) => (
          <button key={v} onClick={() => setView(v)} data-testid={`cs-admin-view-${v}`}
            className={`text-sm px-4 py-1.5 rounded-full border ${view === v ? "gold-gradient text-black border-transparent" : "border-white/15 text-white/60"}`}>{l}</button>
        ))}
      </div>

      {view === "posts" && (
        <div className="space-y-3">
          {posts.length === 0 && <p className="text-white/30 text-sm">Aucun sujet signalé.</p>}
          {posts.map((p) => (
            <div key={p.post_id} className="card-surface p-4" data-testid={`cs-admin-post-${p.post_id}`}>
              <div className="flex items-center gap-2 flex-wrap">
                {p.reports_count > 0 && <span className="text-red-300 text-xs flex items-center gap-1"><Flag size={12} /> {p.reports_count}</span>}
                <span className={`text-[11px] ${p.status === "visible" ? "text-green-400" : "text-white/40"}`}>{p.status}</span>
                <span className="font-medium">{p.title}</span>
              </div>
              {p.body && <p className="text-white/55 text-sm mt-1 line-clamp-2">{p.body}</p>}
              <p className="text-white/25 text-[11px] mt-1">par {p.author_name}</p>
              <div className="flex gap-1.5 flex-wrap mt-2">
                {p.status === "visible" ? <Btn onClick={() => modPost(p.post_id, "hide")} icon={EyeOff} label="Masquer" /> : <Btn onClick={() => modPost(p.post_id, "restore")} icon={RotateCcw} label="Rétablir" tone="green" />}
                <Btn onClick={() => modPost(p.post_id, "remove")} icon={Trash2} label="Supprimer" tone="red" />
                <Btn onClick={() => modPost(p.post_id, "pin")} icon={Pin} label="Épingler" />
              </div>
              <UserActions uid={p.author_id} />
            </div>
          ))}
        </div>
      )}

      {view === "comments" && (
        <div className="space-y-3">
          {comments.length === 0 && <p className="text-white/30 text-sm">Aucun commentaire signalé.</p>}
          {comments.map((c) => (
            <div key={c.comment_id} className="card-surface p-4" data-testid={`cs-admin-comment-${c.comment_id}`}>
              <div className="flex items-center gap-2">
                {c.reports_count > 0 && <span className="text-red-300 text-xs flex items-center gap-1"><Flag size={12} /> {c.reports_count}</span>}
                <span className={`text-[11px] ${c.status === "visible" ? "text-green-400" : "text-white/40"}`}>{c.status}</span>
                <span className="text-sm font-medium">{c.author_name}</span>
              </div>
              <p className="text-white/65 text-sm mt-1">{c.body}</p>
              <div className="flex gap-1.5 flex-wrap mt-2">
                {c.status === "visible" ? <Btn onClick={() => modComment(c.comment_id, "hide")} icon={EyeOff} label="Masquer" /> : <Btn onClick={() => modComment(c.comment_id, "restore")} icon={RotateCcw} label="Rétablir" tone="green" />}
                <Btn onClick={() => modComment(c.comment_id, "remove")} icon={Trash2} label="Supprimer" tone="red" />
              </div>
              <UserActions uid={c.author_id} />
            </div>
          ))}
        </div>
      )}

      {view === "modlog" && (
        <div className="card-surface p-4 space-y-1.5" data-testid="cs-admin-modlog">
          {logs.length === 0 && <p className="text-white/30 text-sm">Aucune action de modération.</p>}
          {logs.map((l) => (
            <p key={l.log_id} className="text-xs text-white/55"><History size={11} className="inline text-gold mr-1" /><span className="text-gold">{l.action}</span> — {l.by} · {new Date(l.at).toLocaleString("fr-FR")}{l.reason ? ` · ${l.reason}` : ""}</p>
          ))}
        </div>
      )}
    </div>
  );
}

const Btn = ({ onClick, icon: Icon, label, tone }) => (
  <button onClick={onClick} className={`inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border transition-colors ${
    tone === "green" ? "border-green-500/30 text-green-300 hover:bg-green-500/10"
      : tone === "red" ? "border-red-500/30 text-red-300 hover:bg-red-500/10"
        : "border-white/15 text-white/70 hover:text-white hover:bg-white/[0.04]"}`}>
    <Icon size={13} /> {label}
  </button>
);
